export const PLAN_CHANGE_TIERS = [
  'entry',
  'starter',
  'basic',
  'lite',
  'essential',
  'essential_plus',
  'premium',
  'premium_plus',
  'lite_scorp_group',
] as const
export type PlanChangeTier = (typeof PLAN_CHANGE_TIERS)[number]

export const isPlanChangeTier = (value: string): value is PlanChangeTier =>
  PLAN_CHANGE_TIERS.some((tier) => tier === value)

const LEGACY_PLAN_CHANGE_TIERS: readonly PlanChangeTier[] = [
  'entry',
  'starter',
  'basic',
]

const SALES_ONLY_PLAN_CHANGE_TIERS: readonly PlanChangeTier[] = [
  'lite_scorp_group',
]

/**
 * The catalog-eligible tiers in lineup order, derived from `PLAN_CHANGE_TIERS` rather than
 * hand-written so a tier added there without touching this file still changes this sequence.
 * Mirrors `CORE_TIERS` in the API
 * (`apps/api/src/api/component/plan_change/plan_change.constants.ts`), with no build step
 * comparing the two.
 */
export const CORE_TIER_SEQUENCE: readonly PlanChangeTier[] =
  PLAN_CHANGE_TIERS.filter(
    (tier) =>
      !LEGACY_PLAN_CHANGE_TIERS.includes(tier) &&
      !SALES_ONLY_PLAN_CHANGE_TIERS.includes(tier)
  )

/** The member's customer type as the API resolves it: it decides which ladder is theirs. */
export type PlanChangeCustomerType = 'solo' | 'scorp_group'

export type PlanCadence = 'monthly' | 'quarterly' | 'annual'
export type ChangeTiming = 'immediate' | 'term_end'
export type PlanChangeDirection = 'up' | 'down' | 'lateral'

/**
 * `errorCode` on a `402` body. Derived server-side from Stripe's card-error code, so the
 * web never sees the Stripe code itself. Everything that is not an authentication step
 * lands on `card_declined`, which the card-update remedy covers.
 */
export const PLAN_CHANGE_PAYMENT_FAILURE_CODES = [
  'card_declined',
  'authentication_required',
] as const
export type PlanChangePaymentFailureCode =
  (typeof PLAN_CHANGE_PAYMENT_FAILURE_CODES)[number]

export const isPaymentFailureCode = (
  value: string | undefined
): value is PlanChangePaymentFailureCode =>
  PLAN_CHANGE_PAYMENT_FAILURE_CODES.some((code) => code === value)

/**
 * `errorCode` on a `500` body. `rolled_back` failed at or before the Stripe mutation, so
 * nothing was charged and a retry is safe. `applied_unconfirmed` means Stripe accepted the
 * change and only the bookkeeping after it failed, so the customer is charged and moved and
 * the reconciler settles the row. An absent or unrecognised code stays outcome-unknown.
 */
export const PLAN_CHANGE_FAILURE_CODES = [
  'rolled_back',
  'applied_unconfirmed',
] as const
export type PlanChangeFailureCode = (typeof PLAN_CHANGE_FAILURE_CODES)[number]

export const isPlanChangeFailureCode = (
  value: string | undefined
): value is PlanChangeFailureCode =>
  PLAN_CHANGE_FAILURE_CODES.some((code) => code === value)

export const PLAN_CHANGE_UNAVAILABLE_CODES = [
  'coupon_scope_unreadable',
] as const
export type PlanChangeUnavailableCode =
  (typeof PLAN_CHANGE_UNAVAILABLE_CODES)[number]

export const isCouponScopeUnreadableCode = (
  value: string | undefined
): value is PlanChangeUnavailableCode =>
  PLAN_CHANGE_UNAVAILABLE_CODES.some((code) => code === value)

const PROMO_CODE_REJECTION_REASONS = [
  'invalid',
  'existing_discount',
  'fixed_amount',
  'ineligible',
] as const
export type PromoCodeRejectionReason =
  (typeof PROMO_CODE_REJECTION_REASONS)[number]

export const isPromoCodeRejectionReason = (
  value: string | undefined
): value is PromoCodeRejectionReason =>
  PROMO_CODE_REJECTION_REASONS.some((code) => code === value)

export const CATALOG_LOOKUP_KEYS = [
  'lite_monthly',
  'lite_annual',
  'essential_monthly',
  'essential_annual',
  'essential_plus_monthly',
  'essential_plus_annual',
  'premium_monthly',
  'premium_annual',
  'premium_plus_monthly',
  'premium_plus_annual',
  'lite_scorp_group_monthly',
  'lite_scorp_group_annual',
] as const
export type CatalogLookupKey = (typeof CATALOG_LOOKUP_KEYS)[number]

export type PlanOptionsUnavailableReason =
  | 'no_subscription'
  | 'trialing'
  | 'cancelling'
  | 'externally_scheduled'
  | 'inactive'
  | 'fixed_discount'
  | 'unresolvable_plan'

export interface CurrentPlan {
  tier: PlanChangeTier
  cadence: PlanCadence
  priceId: string
  listAmountCents: number
  effectiveAmountCents: number
  /** The ongoing discount by its shape ("20% off", "$44 off"); null for a `once` coupon. */
  discountLabel: string | null
  currentPeriodEnd: string
  /** When a deferred change lands: the anniversary for an annual-paid-monthly customer. */
  termEndsAt: string
  inCommittedTerm: boolean
  /** A trialing customer who switches ends the trial and is billed today. */
  isTrialing: boolean
  /** `amount` is a flat coupon: Stripe takes the same dollars off each invoice at any cadence. */
  discountKind: 'amount' | 'percent' | null
  /** When the discount stops applying; null for one that never ends. */
  discountEndsAt: string | null
  /** The customer's own entitlements, which a legacy product states differently from its tier. */
  featureIds: string[] | null
  /** False for a legacy product: one outside the current catalog. */
  onCatalogProduct?: boolean
}

export interface WithheldLossRecord {
  featureId: string
  reason: string
}

export interface PlanChangeTargetPromo {
  label: string
  discountedAmountCents: number
  /** How the coupon runs out: once is a single invoice, forever never reverts. */
  duration: 'once' | 'repeating' | 'forever'
  durationInMonths: number | null
}

export interface PlanChangeTarget {
  tier: PlanChangeTier
  cadence: PlanCadence
  lookupKey: CatalogLookupKey
  priceId: string
  productId: string
  listAmountCents: number
  effectiveAmountCents: number
  /** Whether the ongoing discount covers this target's product. Absent from an older API: read as true. */
  discountApplies?: boolean
  timing: ChangeTiming
  /** The server's ordering of this move from the member's current tier, so the page keeps no tier ladder. */
  direction: PlanChangeDirection
  /** At most one target per cadence carries it, so each tab shows one badge. `targets` arrives in display order. */
  recommended: boolean
  prorationAmountCents: number | null
  /** Credit for unused time (negative) and charge for the rest of the period, both set or both null. */
  prorationCreditCents: number | null
  prorationChargeCents: number | null
  gained: string[]
  marketedGains: string[]
  lost: string[]
  withheld: WithheldLossRecord[]
  promo: PlanChangeTargetPromo | null
}

export interface PendingPlanChange {
  toTier: PlanChangeTier
  toCadence: PlanCadence
  effectiveAt: string
}

/** What a schedule support parked will do. Null alongside `externally_scheduled` when unknown. */
export type ExternalScheduledChange =
  | {
      kind: 'change'
      toTier: PlanChangeTier
      toCadence: PlanCadence
      effectiveAt: string
    }
  | { kind: 'cancel'; effectiveAt: string }

export interface PlanOptions {
  currentPlan: CurrentPlan | null
  targets: PlanChangeTarget[]
  pendingChange: PendingPlanChange | null
  suppressedTargetCount: number
  /** True when a coupon's product scope could not be read; the server refuses changes until it can. */
  couponScopeUnreadable: boolean
  unavailableReason: PlanOptionsUnavailableReason | null
  externalScheduledChange: ExternalScheduledChange | null
  customerType: PlanChangeCustomerType
}

export interface PlanChangeRequest {
  toLookupKey: CatalogLookupKey
  acknowledgedLosses: string[]
  promoCode?: string
}

/** The coupon id never reaches the client: only what the confirm step shows. */
export type PromoCodePreview =
  | {
      status: 'valid'
      label: string
      discountedAmountCents: number
      prorationAmountCents: number
      prorationCreditCents: number | null
      prorationChargeCents: number | null
      duration: 'once' | 'repeating' | 'forever'
      durationInMonths: number | null
    }
  | { status: 'rejected'; reason: PromoCodeRejectionReason; message: string }

export interface AppliedPlanChange {
  planChangeId: number
  status: 'applied' | 'scheduled'
  effectiveAt: string
  invoicedAmountCents: number
  createdAt: string
  toTier: PlanChangeTier | null
  toCadence: PlanCadence | null
  /** Null for a term-end change: nothing bills until the phase swaps at term end. */
  nextBillingAt: string | null
  /** Null together with `cardLast4`. */
  cardBrand: string | null
  cardLast4: string | null
}

export interface PlanCatalogPrice {
  cadence: PlanCadence
  lookupKey: CatalogLookupKey
  priceId: string
  listAmountCents: number
  /** Whether this exact plan is a change this customer could take. False also covers the plan they are on. */
  offered: boolean
}

export interface PlanCatalogTier {
  tier: PlanChangeTier
  featureIds: string[]
  prices: PlanCatalogPrice[]
}

export interface PlanCatalog {
  tiers: PlanCatalogTier[]
}
